import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, DataTable, FilterBar, IconButton, Pill, SelectField, type Column , fmtBRL, fmtDataISO} from "@atlas/ui/web";
import { atlas, enterImpersonate } from "../../../lib/sdk";
import { downloadCsv } from "../../../lib/csv";
import { matchAny } from "../../../lib/text-search";
import type { AdminServidor, ServidorCampoConfig } from "@atlas/sdk";
import { EditModal } from "./_editModal";
import { DEFAULT_CAMPOS_FALLBACK } from "./_defaults";
import { isSuperAdmin, readAverbadoraPermissoesFromJwt } from "../../../lib/averbadora-perms";

// Salario pro CSV em formato BR (sem "R$"): 3692.3 -> "3.692,30". Bate com o
// parser de import (milhar = ponto, decimal = virgula). Antes o export soltava
// "3692.3" e o import, removendo os pontos, lia 36923 — salario errado no
// round-trip export->import.
const fmtMoedaCsv = (n: number): string => {
  const [int, dec] = Math.abs(n).toFixed(2).split(".");
  const milhar = int!.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  return `${n < 0 ? "-" : ""}${milhar},${dec}`;
};
const fmtCpf = (cpf: string) => {
  const d = (cpf ?? "").replace(/\D/g, "");
  return d.length === 11 ? `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}` : (cpf || "—");
};
const fmtTel = (tel?: string) => {
  const d = (tel ?? "").replace(/\D/g, "");
  if (d.length === 11) return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
  if (d.length === 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return d || "—";
};


/** Extrai o valor de um servidor pra qualquer key (built-in ou custom). */
function getValor(s: AdminServidor, key: string): string | number | null | undefined {
  if (key === "cpf") return s.cpf;
  if (key.startsWith("custom_")) return s.camposCustom?.[key];
  return (s as unknown as Record<string, string | number | null | undefined>)[key];
}

/** Converte um campo da config em Column<AdminServidor> com render por tipo. */
/** Renderiza a coluna de acoes. Botao "Entrar como" (🎭) so aparece pro super
 *  admin — operadores criados pelo super admin com acesso a "servidores" veem
 *  apenas o botao Editar (cliente 28/07/2026). Backend tambem enforce via
 *  requireSuperAdmin no /v1/admin/impersonate/servidor/:matricula. */
function AcoesCell({ servidor, onEdit, podeImpersonar }: { servidor: AdminServidor; onEdit: (s: AdminServidor) => void; podeImpersonar: boolean }) {
  const [impersonando, setImpersonando] = useState(false);
  const entrar = async () => {
    setImpersonando(true);
    try {
      const r = await atlas.admin.impersonateServidor(servidor.matricula);
      enterImpersonate({
        parentRole: "averbadora",
        novoAccessToken: r.access_token,
        novoRefreshToken: r.refresh_token,
        servidor: { nome: r.user.nome, matricula: r.user.matricula, cpfMasked: r.user.cpfMasked },
      });
      window.location.assign("/servidor");
    } catch (e) {
      setImpersonando(false);
      alert(`Erro ao entrar como servidor: ${(e as Error).message}`);
    }
  };
  return (
    <div style={{ display: "inline-flex", gap: 4 }}>
      {podeImpersonar ? (
        <IconButton title={`Entrar como ${servidor.nome}`} onClick={() => { if (!impersonando) void entrar(); }}>
          {impersonando ? "…" : "🎭"}
        </IconButton>
      ) : null}
      <IconButton title="Editar" onClick={() => onEdit(servidor)}>✎</IconButton>
    </div>
  );
}

function campoParaColuna(
  campo: ServidorCampoConfig,
  onEdit: (s: AdminServidor) => void,
  podeImpersonar: boolean,
  convenioNomePorId: Map<string, string>,
): Column<AdminServidor> {
  if (campo.key === "acoes") {
    return { key: "acoes", header: "", render: (s) => <AcoesCell servidor={s} onEdit={onEdit} podeImpersonar={podeImpersonar} /> };
  }
  const base: Column<AdminServidor> = {
    key: campo.key,
    header: campo.label,
    // Sem wrap — a tabela mantem scroll horizontal, mesmo padrao da tela antiga.
    // mono desligado (cliente 24/07/2026): tabela usa fonte unica (sans-serif do
    // sistema) em todas as colunas. Antes matricula/cpf/idConvenio saiam em
    // monospace, o que ficava com estetica diferente do resto.
  };
  base.render = (s) => {
    const v = getValor(s, campo.key);
    const dim = <span style={{ color: "var(--text-dim)" }}>—</span>;
    // Vazio/nulo -> "—". Numero==0 tambem trata como vazio (IBGE 0 = nao veio
    // no CSV, salario 0 = nao informado). Mesma regra do servidores.tsx antigo.
    if (v == null || v === "") return dim;
    if (campo.tipo === "numero" && Number(v) === 0) return dim;
    if (campo.tipo === "moeda" && Number(v) === 0) return dim;
    if (campo.key === "cpf") return <>{fmtCpf(String(v))}</>;
    // Convenio: mostra o nome legivel em vez do ID cru (CONV-001 -> "MUNICIPIO
    // DE CAPISTRANO / ATLAS TECH"). Cliente pediu 28/07/2026. Fallback pro ID
    // cru se o lookup nao achou (convenio deletado / lista ainda carregando).
    // Export CSV continua usando o ID cru via getValor — round-trip preservado.
    if (campo.key === "idConvenio") {
      const nome = convenioNomePorId.get(String(v));
      return <>{nome ?? String(v)}</>;
    }
    // Telefone: mesma mascara BR da prefeitura/servidores pra a tabela ficar
    // visualmente identica nos dois perfis.
    if (campo.key === "telefone" || campo.tipo === "telefone") return <>{fmtTel(String(v))}</>;
    if (campo.key === "status") {
      const st = String(v) as AdminServidor["status"];
      return <Pill variant={st === "ativo" ? "averbado" : st === "bloqueado" ? "rejeitada" : "expirado"}>{st}</Pill>;
    }
    // Situacao funcional: Pill verde/pendente/expirada. Mesmo padrao visual
    // da prefeitura/servidores — cliente pediu 24/07/2026 pra alinhar.
    if (campo.key === "situacaoFuncional") {
      const situ = String(v).trim().toUpperCase();
      if (!situ) return <span style={{ color: "var(--text-dim)", fontSize: 12 }}>NÃO INFORMADO</span>;
      const variant: "expirado" | "averbado" | "pendente" =
        /desligado|aposentad/i.test(situ) ? "expirado"
        : /afastad|licenc|ferias/i.test(situ) ? "pendente"
        : "averbado";
      return <Pill variant={variant}>{situ}</Pill>;
    }
    switch (campo.tipo) {
      case "moeda":
        return typeof v === "number" ? fmtBRL(v) : fmtBRL(Number(v) || 0);
      case "numero":
        return String(v);
      // Data ISO (YYYY-MM-DD) -> DD/MM/YYYY. Cobre dataAdmissao/dataNascimento
      // e qualquer custom com tipo="data". Cliente 28/07/2026.
      case "data":
        return fmtDataISO(String(v));
      default:
        return String(v);
    }
  };
  if (campo.tipo === "moeda" || campo.tipo === "numero") base.align = "right";
  return base;
}

export function AdminServidoresVisualizar() {
  const qc = useQueryClient();
  const prefeituras = useQuery({ queryKey: ["admin", "prefeituras"], queryFn: () => atlas.admin.listPrefeituras() });
  const [prefId, setPrefId] = useState<string>("");
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState<AdminServidor | null>(null);
  const [limparAberto, setLimparAberto] = useState(false);

  const prefIdNum = Number(prefId);
  const prefSelecionada = (prefeituras.data?.prefeituras ?? []).find((p) => String(p.id) === prefId);

  const configQ = useQuery({
    queryKey: ["admin", "servidor-campos-config", prefIdNum],
    queryFn: () => atlas.admin.getServidorCamposConfig(prefIdNum),
    enabled: !!prefId,
  });

  const dataQ = useQuery({
    queryKey: ["admin", "servidores", prefId],
    queryFn: () => atlas.admin.listServidores({ prefeitura_id: prefIdNum }),
    enabled: !!prefId,
  });

  // Lookup nome do convenio pelo ID (pra tabela renderizar legivel em vez de
  // "CONV-001"). staleTime alto: convenio muda raramente. Cliente 28/07/2026.
  const conveniosQ = useQuery({
    queryKey: ["admin", "convenios"],
    queryFn: () => atlas.admin.listConvenios(),
    staleTime: 5 * 60_000,
  });
  const convenioNomePorId = useMemo(() => {
    const m = new Map<string, string>();
    for (const cv of conveniosQ.data?.convenios ?? []) m.set(cv.id, cv.nome);
    return m;
  }, [conveniosQ.data?.convenios]);

  // Busca "LIKE PHP" via matchAny — case/acento-insensitive, multi-termo AND,
  // varre TODOS os campos do objeto (nome, matricula, cpf, cpfMasked, email,
  // telefone, cargo, vinculo, situacao, salario, convenio, IBGE, datas etc)
  // e trata CPF/telefone/matricula com ou sem pontuacao ("580.886.363-53"
  // acha com "58088" tambem). Ordem "mais recente no topo" vem do BACKEND
  // (criadoEmIso/_dbId) — antes ordenava por numero da matricula aqui, o que
  // so parecia recente por coincidencia. So filtra a busca.
  const filtered = useMemo(
    () => (dataQ.data?.servidores ?? []).filter((s) => matchAny(s, search)),
    [dataQ.data?.servidores, search],
  );

  const total = filtered.length;
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  useEffect(() => { setPage(1); }, [search, prefId, pageSize]);
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const pageSafe = Math.min(page, totalPages);
  const pageStart = (pageSafe - 1) * pageSize;
  const pageRows = filtered.slice(pageStart, pageStart + pageSize);

  // Le permissoes do JWT uma vez (novo token = novo mount da tela apos refresh).
  // isSuperAdmin trata JWT sem claim (dev-user admin@atlas.test) como super.
  const podeImpersonar = useMemo(() => {
    try {
      const stored = window.localStorage.getItem("atlas:tokens");
      const token = stored ? JSON.parse(stored)?.access_token : null;
      return isSuperAdmin(readAverbadoraPermissoesFromJwt(token));
    } catch { return false; }
  }, []);

  const columns: Column<AdminServidor>[] = useMemo(() => {
    const config = configQ.data?.config;
    // Fallback: se o endpoint de config nao existe ainda (backend nao deployado)
    // ou falhou, monta colunas built-in default equivalentes ao antigo servidores.tsx.
    // Assim a tela nao aparece em branco enquanto a API nao tem os novos endpoints.
    if (!config) {
      return DEFAULT_CAMPOS_FALLBACK
        .map((c) => campoParaColuna(c, setEditing, podeImpersonar, convenioNomePorId))
        .concat([{ key: "acoes", header: "", render: (s) => <AcoesCell servidor={s} onEdit={setEditing} podeImpersonar={podeImpersonar} /> }]);
    }
    const visiveis = config.campos.filter((c) => c.visivel).sort((a, b) => a.ordem - b.ordem);
    const cols = visiveis.map((c) => campoParaColuna(c, setEditing, podeImpersonar, convenioNomePorId));
    cols.push({ key: "acoes", header: "", render: (s) => <AcoesCell servidor={s} onEdit={setEditing} podeImpersonar={podeImpersonar} /> });
    return cols;
  }, [configQ.data?.config, podeImpersonar, convenioNomePorId]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <header>
        <span style={{ fontSize: 12, letterSpacing: "0.1em", fontWeight: 700, color: "var(--text-dim)", textTransform: "uppercase" }}>
          Averbadora
        </span>
        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", justifyContent: "space-between" }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 12, flexWrap: "wrap" }}>
            <h1 style={{ margin: "4px 0 0", fontSize: "1.6rem" }}>Ver servidores</h1>
            <span style={{ fontSize: 13, color: "var(--text-muted)" }}>
              {prefId ? (dataQ.isLoading ? "Carregando…" : `${total} servidor${total === 1 ? "" : "es"}`) : "Selecione uma prefeitura"}
            </span>
          </div>
          {/* Limpar linhas de teste/ficcao ("ZZ OBSOLETA - NAO USAR") — abre modal
              que pede a senha (ADMIN_PURGE_PASSWORD do env). Quem executa e' o
              usuario digitando a senha aqui; destrava o kill-switch + apaga por
              nome (so a ficcao sai, os reais ficam). Cliente pediu 24/07/2026. */}
          <Button variant="ghost" onClick={() => setLimparAberto(true)}>Limpar Servidores</Button>
        </div>
      </header>
      {limparAberto ? <LimparObsoletosModal onClose={() => setLimparAberto(false)} onDone={() => { setLimparAberto(false); qc.invalidateQueries({ queryKey: ["admin"] }); qc.invalidateQueries({ queryKey: ["prefeitura"] }); }} /> : null}

      <FilterBar
        searchValue={search}
        onSearchChange={setSearch}
        onReset={() => { setSearch(""); setPrefId(""); }}
        onExport={total > 0 ? () => downloadCsv(
          `servidores-${prefSelecionada?.nome ?? "todos"}.csv`,
          filtered.map((s) => {
            const row: Record<string, string | number> = {};
            const config = configQ.data?.config;
            const visiveis = config ? config.campos.filter((c) => c.visivel).sort((a, b) => a.ordem - b.ordem) : [];
            for (const c of visiveis) {
              const v = getValor(s, c.key);
              if (v == null || v === "") { row[c.key] = ""; continue; }
              if (c.tipo === "moeda") {
                // Sempre BR "3.692,30" pra o import ler certo no round-trip.
                const n = typeof v === "number" ? v : Number(String(v).replace(/[^\d,.-]/g, "").replace(/\./g, "").replace(",", "."));
                row[c.key] = Number.isFinite(n) ? fmtMoedaCsv(n) : String(v);
              } else {
                row[c.key] = typeof v === "number" ? v : String(v);
              }
            }
            return row;
          }),
        ) : undefined}
      >
        <div style={{ maxWidth: 320 }}>
          <SelectField
            label="Prefeitura"
            value={prefId}
            onChange={(e) => setPrefId(e.target.value)}
            options={[{ value: "", label: "— Selecione —" }, ...((prefeituras.data?.prefeituras ?? []).map((p) => ({ value: String(p.id), label: `${p.nome}/${p.uf}` })))]}
          />
        </div>
      </FilterBar>

      {!prefId ? (
        <div style={{ padding: 36, textAlign: "center", color: "var(--text-muted)", border: "1px solid var(--border)", borderRadius: 12 }}>
          Selecione uma prefeitura acima para listar os servidores.
        </div>
      ) : (
        <>
          <DataTable columns={columns} rows={pageRows} rowKey={(s) => String(s.id)} loading={dataQ.isLoading || configQ.isLoading} />

          {total > 0 ? (
            <div
              style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                gap: 12, flexWrap: "wrap",
                padding: "10px 14px", background: "var(--bg-elev-1)",
                border: "1px solid var(--border)", borderRadius: 10, fontSize: 13,
              }}
            >
              <div style={{ color: "var(--text-muted)" }}>
                Mostrando <b style={{ color: "var(--text)" }}>{pageStart + 1}</b>–
                <b style={{ color: "var(--text)" }}>{Math.min(pageStart + pageSize, total)}</b>
                {" "}de <b style={{ color: "var(--text)" }}>{total.toLocaleString("pt-BR")}</b>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <label style={{ color: "var(--text-muted)", fontSize: 12 }}>
                  Por página:
                  <select
                    value={pageSize}
                    onChange={(e) => setPageSize(Number(e.target.value))}
                    style={{
                      marginLeft: 6, padding: "4px 8px", borderRadius: 6,
                      background: "var(--surface)", color: "var(--text)",
                      border: "1px solid var(--border-strong)", fontSize: 12,
                    }}
                  >
                    {[25, 50, 100, 200].map((n) => <option key={n} value={n}>{n}</option>)}
                  </select>
                </label>
                <Button size="sm" variant="ghost" onClick={() => setPage(1)} disabled={pageSafe <= 1}>«</Button>
                <Button size="sm" variant="ghost" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={pageSafe <= 1}>‹ Anterior</Button>
                <span style={{ color: "var(--text-muted)", fontSize: 12, minWidth: 90, textAlign: "center" }}>
                  Página <b style={{ color: "var(--text)" }}>{pageSafe}</b> de <b style={{ color: "var(--text)" }}>{totalPages}</b>
                </span>
                <Button size="sm" variant="ghost" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={pageSafe >= totalPages}>Próxima ›</Button>
                <Button size="sm" variant="ghost" onClick={() => setPage(totalPages)} disabled={pageSafe >= totalPages}>»</Button>
              </div>
            </div>
          ) : null}
        </>
      )}

      {editing ? (
        <EditModal
          servidor={editing}
          prefeituraId={prefIdNum}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); qc.invalidateQueries({ queryKey: ["admin", "servidores"] }); }}
        />
      ) : null}
    </div>
  );
}

/** Modal pra apagar as linhas de teste/ficcao "ZZ OBSOLETA - NAO USAR".
 *  Pede a senha (ADMIN_PURGE_PASSWORD do env). O fluxo: destrava o kill-switch
 *  com a senha, depois apaga por NOME (so a ficcao; os reais com a mesma
 *  matricula ficam). Quem executa e' o usuario digitando a senha aqui. */
function LimparObsoletosModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const NOME_ALVO = "ZZ OBSOLETA - NAO USAR";
  const [senha, setSenha] = useState("");
  const [show, setShow] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const executar = async () => {
    if (!senha) { setErro("Informe a senha."); return; }
    setEnviando(true);
    setErro(null);
    try {
      // 1) destrava o kill-switch com a senha; 2) apaga por nome.
      await atlas.admin.destravarDestrutivos(senha, 120);
      const r = await atlas.admin.deleteServidoresPorNome([NOME_ALVO], senha);
      alert(`Pronto. ${r.removidos} servidor(es) "${NOME_ALVO}" removido(s).`);
      onDone();
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Falha ao remover.";
      setErro(/senha inv/i.test(msg) ? "Senha incorreta." : msg);
    } finally {
      setEnviando(false);
    }
  };
  return (
    <div
      onMouseDown={enviando ? undefined : (e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 16 }}
    >
      <div onMouseDown={(e) => e.stopPropagation()} style={{ background: "var(--surface)", border: "1px solid var(--border-strong)", borderRadius: 16, padding: 24, maxWidth: 460, width: "100%", display: "flex", flexDirection: "column", gap: 14 }}>
        <h3 style={{ margin: 0, color: "var(--danger-500)" }}>⚠ Remover linhas de teste</h3>
        <p style={{ color: "var(--text-muted)", fontSize: 14, margin: 0 }}>
          Apaga <b style={{ color: "var(--danger-500)" }}>todos</b> os servidores com nome <b>"{NOME_ALVO}"</b>. Os servidores reais (mesmo com matrícula igual) NÃO são afetados — a exclusão é pelo nome. Ação irreversível.
        </p>
        <div>
          <label style={{ fontSize: 11, fontWeight: 700, letterSpacing: ".06em", color: "var(--text-dim)", textTransform: "uppercase", display: "block", marginBottom: 6 }}>
            Senha de confirmação <span style={{ color: "var(--danger-500)" }}>*</span>
          </label>
          <div style={{ position: "relative" }}>
            <input
              type={show ? "text" : "password"}
              value={senha}
              onChange={(e) => setSenha(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !enviando) executar(); }}
              autoFocus
              placeholder="ADMIN_PURGE_PASSWORD (do env)"
              style={{ width: "100%", boxSizing: "border-box", background: "var(--bg-elev)", border: `1px solid ${erro ? "var(--danger-500)" : "var(--border-strong)"}`, borderRadius: 10, padding: "10px 40px 10px 14px", color: "var(--text)", fontSize: 14, outline: "none" }}
            />
            <button type="button" onClick={() => setShow((s) => !s)} style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "var(--text-dim)", fontSize: 13 }} aria-label={show ? "Ocultar senha" : "Mostrar senha"}>{show ? "🙈" : "👁"}</button>
          </div>
          {erro ? <span style={{ fontSize: 12, color: "var(--danger-500)", marginTop: 4, display: "block" }}>{erro}</span> : null}
        </div>
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <Button variant="ghost" onClick={onClose} disabled={enviando}>Cancelar</Button>
          <Button onClick={executar} disabled={enviando}>{enviando ? "Removendo…" : "Remover"}</Button>
        </div>
      </div>
    </div>
  );
}
